/**
 * HM-386 cluster login PIN: pagina fallback /login-admin per email+password.
 * Il flow default reception e' /login con PIN (HM-386..388). Questa pagina
 * resta per accesso admin/debug e per cluster 25 RLS che la userà per
 * onboarding nuovi tenant.
 *
 * Era ex pages/Login.tsx; rinominata e ridestinata su route /login-admin.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { useAuth } from '../stores/auth.js';

export function LoginAdminPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setUser = useAuth((s) => s.setUser);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: async (res) => {
      setUser(res.user);
      await queryClient.invalidateQueries();
      const from =
        searchParams.get('from') ?? (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate({ email: email.trim().toLowerCase(), password });
  };

  const errMsg = mutation.error ? mapLoginError(mutation.error) : null;

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        fontFamily: 'system-ui, sans-serif',
        background: '#0f172a',
        color: '#f8fafc',
      }}
    >
      <form
        onSubmit={onSubmit}
        aria-label="login-form"
        style={{
          background: '#1e293b',
          padding: '2rem',
          borderRadius: 8,
          width: 'min(360px, 92vw)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>HOTEL MANAGER · Reception</h1>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>Password</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </label>
        {errMsg && (
          <p role="alert" style={{ color: '#fca5a5', margin: 0 }}>
            {errMsg}
          </p>
        )}
        <button
          type="submit"
          disabled={mutation.isPending}
          style={{
            padding: '0.75rem',
            background: '#22c55e',
            border: 'none',
            color: '#fff',
            fontWeight: 600,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          {mutation.isPending ? 'Accesso in corso…' : 'ACCEDI'}
        </button>
        <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
          Hai dimenticato la password? Contatta l'amministratore.
        </p>
      </form>
    </main>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '0.5rem',
  borderRadius: 4,
  border: '1px solid #334155',
  background: '#0f172a',
  color: '#f8fafc',
  fontSize: '1rem',
};

function mapLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Email o password non validi.';
    if (err.status === 429) return 'Troppi tentativi. Riprova tra un minuto.';
    if (err.status === 400) return 'Dati non validi.';
  }
  return 'Errore di connessione. Riprova.';
}
